<template>
    <div id="checkout-summary-wrapper" :class="{ open: showProducts }">
        <div id="checkout-summary-overlay" @click="showProducts = false"></div>
        <div id="checkout-summary" class="mt-4 ml-4">
            <h3 class="mt hidden-sm-down newLayoutTitleFonts text-uppercase">
                {{ $t('_shop.yourBag') }}
            </h3>

            <div
                id="checkout-summary-mobile-header"
                ref="headerMobile"
                class="d-flex flex-align-center text-bold hidden-xs-up"
                @click="showProducts = !showProducts"
            >
                <p class="h3 mx-0 my-0">
                    <span>Total:&nbsp;</span>
                    <span v-if="this.cart.total_paid > 0">
                        {{ $f.formatMoney(this.cart.total_paid) }}
                    </span>
                    <span v-else class="free-price"> {{ $t('free') }} </span>
                </p>
                <p class="ml-auto my-0 text-right">
                    <span class="text-accent text-yanone">
                        <span v-show="!showProducts">{{ $t('toSeeDetails') }}</span>
                        <span v-show="showProducts">{{ $t('toHide') }}</span>
                    </span>
                    <i
                        :class="{
                            'icon-chevron-up': !showProducts,
                            'icon-chevron-down': showProducts,
                        }"
                        class="icon-cr accent"
                    ></i>
                </p>
            </div>

            <div id="checkout-summary-content">
                <template v-for="item in summaryItems">
                    <div class="d-flex">
                        <p class="text-uppercase">{{ item.label }}</p>
                        <p class="ml-auto text-right" :class="item.class">
                            {{ item.value }}
                        </p>
                    </div>
                </template>

                <div
                    v-if="
                        $scope.IS_REGULAR_CUSTOMER ||
                        ($scope.wholesalerDiscounts && $scope.wholesalerDiscounts.length > 0)
                    "
                    class="d-flex cursor-pointer"
                    @click="showDiscounts = !showDiscounts"
                >
                    <p id="checkout-summary-discounts-toggle">
                        <span>{{ $t('_shop.discounts') }}</span>
                        <i
                            :class="['icon-chevron-' + (showDiscounts ? 'up' : 'down')]"
                            class="icon-cr accent"
                        ></i>
                    </p>
                    <p class="ml-auto text-right">
                        <span v-if="cart.total_discounts <= 0">
                            {{ $t('_checkout._discount.haveDiscountClickHere') }}
                        </span>
                    </p>
                </div>

                <div id="checkout-discounts">
                    <div :class="{ opened: showDiscounts }" class="collapsible">
                        <div :class="{ busy: discountsBusy }" class="frame-outline mb">
                            <Alert :alert="discountAlert" />

                            <template v-if="$scope.IS_REGULAR_CUSTOMER">
                                <p class="mt-0">
                                    {{ $t('_checkout._discount.haveDiscountOrGiftCode') }}
                                </p>
                                <div class="form-inline">
                                    <input
                                        v-model="typedDiscountName"
                                        :aria-label="$t('_shop._discount.code')"
                                        :placeholder="$t('_shop._discount.code')"
                                        type="text"
                                        @keydown.enter="addDiscount"
                                    />
                                    <input
                                        :disabled="!typedDiscountName"
                                        class="btn"
                                        type="button"
                                        :value="$t('toAdd')"
                                        @click="addDiscount"
                                    />
                                </div>
                            </template>
                            <template
                                v-else-if="
                                    $scope.wholesalerDiscounts &&
                                    $scope.wholesalerDiscounts.length > 0
                                "
                            >
                                <div
                                    v-for="wholesalerDiscount in $scope.wholesalerDiscounts"
                                    v-if="
                                        !cart.discounts.data ||
                                        cart.discounts.data.filter(
                                            (d) => d.name === wholesalerDiscount.name
                                        ).length === 0
                                    "
                                    class="alert-success my cursor-pointer"
                                    @click="addWholesalerDiscount(wholesalerDiscount)"
                                >
                                    <p>
                                        {{
                                            $t('_checkout._discount.clickToAddGivenDiscount', [
                                                wholesalerDiscount.id_discount_type == 2
                                                    ? $f.formatMoney(wholesalerDiscount.value)
                                                    : `${wholesalerDiscount.value}%`,
                                            ])
                                        }}
                                    </p>
                                    <p>
                                        {{ $t('discount') }}:
                                        {{ wholesalerDiscount.name }}
                                        <span
                                            v-if="
                                                wholesalerDiscount.troca_id &&
                                                wholesalerDiscount.troca_id > 0
                                            "
                                        >
                                            ({{ $t('exchange') }} {{ wholesalerDiscount.troca_id }})
                                        </span>
                                    </p>
                                </div>
                            </template>

                            <template v-if="((cart.discounts || {}).data || {}).length > 0">
                                <hr v-if="$scope.IS_REGULAR_CUSTOMER" />

                                <p>{{ $t('_checkout._discount.appliedDiscounts') }}:</p>
                                <ul class="d-flex flex-spaced-fixed flex-wrap">
                                    <li
                                        v-for="discount in cart.discounts.data"
                                        class="flex-child-fixed"
                                    >
                                        <a @click="removeDiscount(discount)">
                                            <div class="chip chip-close">
                                                {{ discount.name }}
                                            </div>
                                        </a>
                                    </li>
                                </ul>
                            </template>
                        </div>
                    </div>
                </div>

                <hr class="mx-0 my-0" />

                <hr class="mx-0 my-0" />

                <div>
                    <div class="d-flex text-bold">
                        <p class="text-uppercase">{{ $t('total') }}</p>
                        <p class="ml-auto text-right">
                            <span v-if="cart.total_paid > 0">
                                {{ $f.formatMoney(cart.total_paid) }}
                            </span>
                            <span v-else class="free-price">
                                {{ $t('free') }}
                            </span>
                        </p>
                    </div>
                </div>

                <div
                    class="mt-3 mb-4 cashback-checkout text-uppercase d-flex flex-justify-between flex-align-center"
                    v-if="$scope.IS_REGULAR_CUSTOMER && cart.total_cashback_generated > 0"
                >
                    <p class="text-bold">
                        Você ganha {{ $f.formatMoney(cart.total_cashback_generated) }} de cashback
                        <i
                            class="icon-cr icon-cashback mr-1 text-cashback"
                            style="font-size: 20px"
                        />
                    </p>

                    <u @click="cashbackRules = true" class="cursor-pointer">Regras</u>
                </div>

                <div class="d-flex">
                    <p id="checkout-summary-products-toggle">
                        <span class="h3 newLayoutTitleFonts text-uppercase"
                            >{{ $t('products') }} ({{ cart.count_products }})</span
                        >
                    </p>
                </div>

                <div id="checkout-products" style="overflow: auto">
                    <span class="text-danger" v-if="hasCustomizableProduct"
                        >Produtos personalizados não são passíveis de troca e / ou devolução, salvo
                        em caso de defeitos de fabricação.</span
                    >

                    <p v-if="$scope.showSizeNotice && $scope.TENANT_IDENTIFIER === 'chicorei'">
                        Nossos tamanhos mudaram desde sua última compra. Confira as medidas no
                        produto
                    </p>
                    <div :class="{ opened: showProductsList }" class="collapsible">
                        <ul class="checkout-product-list mb">
                            <li
                                v-for="product in cart.products.data"
                                :key="`${product.produto_id}_${product.variation_id}`"
                                class="product-list-item text-yanone mb"
                            >
                                <img
                                    v-if="getProductImage(product) && !$scope.showSizeNotice"
                                    :alt="product.product.name"
                                    :src="`${getProductImage(product)}&w=65&fit=crop`"
                                    class="mr"
                                    width="85"
                                />
                                <img
                                    v-else-if="getProductImage(product) && $scope.showSizeNotice"
                                    :alt="product.product.name"
                                    :src="`${getProductImage(product)}&w=65&fit=crop`"
                                    class="mr cursor-pointer"
                                    width="85"
                                    @click="getProductLink(product)"
                                />
                                <div class="d-flex flex-column ml-4">
                                    <span v-if="$scope.TENANT_IDENTIFIER !== `chicorei`">
                                        {{ product.product.name }}
                                    </span>
                                    <span
                                        v-if="
                                            product.size !== 'Padrão' &&
                                            (product.product.type || {}).url !== 'vale-presente'
                                        "
                                    >
                                        <span
                                            v-if="(product.product.type || {}).url === 'assinatura'"
                                        >
                                            {{ $t('period') }}:
                                        </span>
                                        <span
                                            class="newLayoutTextFont"
                                            v-if="product.product_model_name"
                                            >{{ product.product_model_name }} -
                                        </span>
                                        <strong class="newLayoutTextFont">{{
                                            product.size
                                        }}</strong>
                                    </span>

                                    <span>
                                        <span
                                            v-if="product.product_color_name || product.color_name"
                                        >
                                            {{ product.product_color_name || product.color_name }}
                                        </span>
                                        <a
                                            v-if="(product.product.type || {}).url === 'assinatura'"
                                            href="central-de-ajuda/#reamaze#0#/kb/prime"
                                            target="_blank"
                                        >
                                            {{ $t('checkTerms') }}
                                        </a>
                                    </span>
                                    <span v-if="!product.can_be_bought">
                                        <span>{{ $t('unavailable') }}</span>
                                    </span>

                                    <template v-else>
                                        <span
                                            v-if="
                                                ['vale-presente', 'assinatura'].indexOf(
                                                    (product.product.type || {}).url
                                                ) === -1
                                            "
                                        >
                                            <span v-if="product.in_stock >= product.quantity">
                                                {{ $t('_shop._stock.inStock') }}
                                            </span>
                                            <span v-else-if="product.product.virtual_stock">
                                                {{ $t('_shop.onDemandProduction') }}
                                            </span>
                                        </span>

                                        <span>
                                            <span
                                                v-if="
                                                    (product.product.is_active &&
                                                        !product.has_quantity) ||
                                                    !product.product.in_stock
                                                "
                                            >
                                                {{ $t('_shop._stock.qtyUnavailable') }}
                                            </span>
                                            <span v-else-if="!product.product.is_active">
                                                {{ $t('unavailable') }}
                                            </span>
                                        </span>
                                    </template>

                                    <p
                                        class="text-bold"
                                        v-if="product.product.is_gift && product.product.price <= 1"
                                    >
                                        {{ product.quantity }} x Grátis
                                    </p>

                                    <p class="text-bold" v-else>
                                        {{ product.quantity }} x
                                        {{ $f.formatMoney(product.total_price) }}
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <Modal v-model="cashbackRules">
            <template v-slot:title>Regras Cashback</template>
            <div class="card-block">
                <CashbackFaq :is-hotsite="false" />
            </div>
        </Modal>
    </div>
</template>

<script>
    import BaseCheckoutSummary from './common/BaseCheckoutSummary.vue';
    import Alert from '../surface/alert/Alert.vue';
    import Modal from '../surface/modal/Modal.vue';
    import CashbackFaq from '../cashback/CashbackFaq.vue';

    export default {
        name: 'CheckoutSummary',

        extends: BaseCheckoutSummary,

        components: { Alert, Modal, CashbackFaq },

        props: {
            hasCustomizableProduct: {
                type: Boolean,
                required: true,
            },
        },

        data() {
            return {
                cashbackRules: false,
            };
        },
    };
</script>
